import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UsersThree, Trophy, CaretUp, CaretDown, Minus, X, Scales, Prohibit } from "@phosphor-icons/react";
import api, { apiErr } from "../api";

export default function Rivals() {
  const [data, setData] = useState(null);
  const [compare, setCompare] = useState(null);
  const [blocks, setBlocks] = useState([]);

  const load = () => {
    api.get("/rivals").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/rivals/blocks").then((r) => setBlocks(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const unfollow = async (id) => {
    try { await api.delete(`/rivals/follow/${id}`); toast.success("Rival removed"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const block = async (id) => {
    try { await api.post("/rivals/block", { user_id: id }); toast.success("Explorer blocked"); setCompare(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const unblock = async (id) => {
    try { await api.delete(`/rivals/block/${id}`); toast.success("Unblocked"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openCompare = async (id) => {
    try { const { data } = await api.get(`/rivals/compare/${id}`); setCompare(data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!data) return <div className="py-20 text-center text-ff-muted" data-testid="rivals-loading">Gathering your rivals…</div>;

  return (
    <div className="space-y-6" data-testid="rivals-page">
      <div className="flex items-center gap-2">
        <UsersThree size={26} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Rival Explorers</h1>
      </div>

      <div className="ff-panel rounded-md p-4" data-testid="rivals-summary">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <span className="text-xs uppercase tracking-widest text-ff-brass">Your Standing</span>
            <div className="font-display text-2xl font-bold text-ff-gold">
              {data.me.position ? `#${data.me.position}` : "Unranked"} <span className="text-base text-ff-muted">· {data.me.frontier_score} Frontier Score</span>
            </div>
          </div>
          <div className="text-right">
            <span className="text-xs uppercase tracking-widest text-ff-brass">Rivals Followed</span>
            <div className="font-display text-2xl font-bold text-ff-parchment" data-testid="rivals-count">{data.count}/{data.max}</div>
          </div>
        </div>
        {data.highest_among_rivals && data.count > 0 && (
          <p className="mt-2 text-sm text-ff-gold" data-testid="rivals-highest">You are the highest-ranked explorer among your rivals.</p>
        )}
      </div>

      {data.count === 0 ? (
        <div className="ff-panel rounded-md p-8 text-center" data-testid="rivals-empty">
          <p className="text-ff-muted">You aren't following any rivals yet.</p>
          <Link to="/leaderboards" className="mt-3 inline-block rounded-sm bg-ff-gold px-4 py-2 font-semibold text-ff-bg hover:bg-ff-brass">Find rivals on the leaderboards</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {data.rivals.map((r) => (
            <div key={r.user_id} className="ff-panel rounded-md p-4" data-testid={`rival-card-${r.user_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg font-semibold text-ff-parchment">{r.name}</div>
                  <div className="text-xs text-ff-brass">{r.public_title || r.rank}</div>
                </div>
                <div className="text-right">
                  <div className="flex items-center justify-end gap-1 text-sm">
                    {r.ahead_of_you ? <CaretUp size={14} className="text-ff-danger" /> : <CaretDown size={14} className="text-ff-gold" />}
                    <span className={r.ahead_of_you ? "text-ff-danger" : "text-ff-gold"}>{r.position ? `#${r.position}` : "—"}</span>
                  </div>
                  <div className="text-xs text-ff-muted">{r.frontier_score} FS</div>
                </div>
              </div>
              <div className="mt-2 text-xs text-ff-muted">
                {r.ahead_of_you ? `Leads you by ${Math.abs(r.score_diff)} Frontier Score` : `You lead by ${Math.abs(r.score_diff)} Frontier Score`}
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => openCompare(r.user_id)} data-testid={`rival-compare-${r.user_id}`} className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-xs text-ff-parchment hover:border-ff-gold hover:text-ff-gold"><Scales size={14} /> Compare</button>
                <button onClick={() => unfollow(r.user_id)} data-testid={`rival-unfollow-${r.user_id}`} className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-xs text-ff-muted hover:text-ff-parchment"><X size={14} /> Unfollow</button>
                <button onClick={() => block(r.user_id)} data-testid={`rival-block-${r.user_id}`} className="flex items-center gap-1 rounded-sm border border-ff-danger/30 px-3 py-1.5 text-xs text-ff-danger/90 hover:border-ff-danger"><Prohibit size={14} /> Block</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {blocks.length > 0 && (
        <div className="ff-panel rounded-md p-4" data-testid="rivals-blocklist">
          <h3 className="mb-2 font-display text-lg font-semibold">Blocked Explorers</h3>
          {blocks.map((b) => (
            <div key={b.user_id} className="flex items-center justify-between border-b border-ff-brass/10 py-2 text-sm">
              <span className="text-ff-muted">{b.name}</span>
              <button onClick={() => unblock(b.user_id)} data-testid={`unblock-${b.user_id}`} className="rounded-sm border border-ff-brass/30 px-2 py-1 text-xs hover:text-ff-gold">Unblock</button>
            </div>
          ))}
        </div>
      )}

      {compare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setCompare(null)} data-testid="rival-compare-modal">
          <div className="ff-panel max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-md p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold text-ff-gold">You vs. {compare.rival.name}</h3>
              <button onClick={() => setCompare(null)} className="text-ff-muted hover:text-ff-parchment"><X size={20} /></button>
            </div>
            <p className="mb-3 text-sm text-ff-brass">{compare.momentum}</p>
            {compare.prestige && (
              <div className="mb-3 rounded-md border border-ff-brass/25 bg-ff-bg/60 p-3" data-testid="rival-prestige-banner">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-ff-brass">
                  <span>You{compare.prestige.you_tier ? ` · ${compare.prestige.you_tier}` : ""}</span>
                  <span>Prestige</span>
                  <span>{compare.rival.name}{compare.prestige.rival_tier ? ` · ${compare.prestige.rival_tier}` : ""}</span>
                </div>
                <div className="mt-1 flex items-center justify-between font-display text-2xl font-bold">
                  <span className={compare.prestige.you >= compare.prestige.rival ? "text-ff-gold" : "text-ff-parchment"} data-testid="rival-prestige-you">{compare.prestige.you}</span>
                  <span className="text-xs text-ff-muted">{compare.positions?.you ? `#${compare.positions.you}` : "—"} vs {compare.positions?.rival ? `#${compare.positions.rival}` : "—"}</span>
                  <span className={compare.prestige.rival > compare.prestige.you ? "text-ff-gold" : "text-ff-parchment"} data-testid="rival-prestige-rival">{compare.prestige.rival}</span>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {compare.metrics.map((m) => (
                <div key={m.stat} className="rounded-sm border border-ff-brass/20 bg-ff-bg/50 p-2.5" data-testid={`compare-metric-${m.stat.replace(/\s/g,'-').toLowerCase()}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-ff-muted">{m.stat}</span>
                    <span className="font-mono-ff text-ff-parchment">{m.you} <span className="text-ff-muted">vs</span> {m.rival}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-ff-brass">{m.note}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
